import type { Material, Project } from '../types';
import { defaultMaterials } from '../materials/catalog';

const removedWoodDecors = new Set(['oak', 'walnut', 'plywood']);
const normalizeFurnitureMaterials = (furniture: Project['furniture']) => furniture.map((item) => ({
  ...item,
  materialId: removedWoodDecors.has(item.materialId) ? 'white' : item.materialId,
  doors: item.doors ? { ...item.doors, open: item.doors.open ?? false } : { enabled: false, count: 0 as const, open: false, gap: 2, thickness: 18, opening: 'left' as const },
  drawers: item.drawers ? { ...item.drawers, open: item.drawers.open ?? false } : { count: 0, open: false, frontHeight: 160, gap: 2, depth: 450 },
  panelOverrides: Object.fromEntries(Object.entries(item.panelOverrides ?? {}).map(([id, override]) => [id, removedWoodDecors.has(override.materialId) ? { ...override, materialId: 'white' } : override])),
}));

const normalizeMaterials = (materials: Material[]) => {
  const kept = materials.filter((material) => !removedWoodDecors.has(material.id));
  return defaultMaterials.reduce((all, material) => all.some((entry) => entry.id === material.id) ? all : [...all, structuredClone(material)], kept);
};

export function migrateProject(raw: Project): Project {
  if (raw.schemaVersion === 2) return { ...raw, origin: raw.origin ?? [0, 0, 0], room: { ...raw.room, visible: raw.room.visible !== false }, drawings: raw.drawings ?? [], groups: raw.groups ?? [], materials: normalizeMaterials(raw.materials), furniture: normalizeFurnitureMaterials(raw.furniture) };
  if ((raw as { schemaVersion?: number }).schemaVersion === 1) {
    const legacy = raw as unknown as Omit<Project, 'schemaVersion'> & { schemaVersion: 1; materials: Array<Partial<Material> & { id: string; code?: string }> };
    return {
      ...legacy,
      schemaVersion: 2,
      origin: [0, 0, 0],
      materials: normalizeMaterials(legacy.materials.map((material) => ({
        ...(defaultMaterials.find((entry) => entry.id === material.id) ?? defaultMaterials[0]),
        ...material,
        decorCode: material.decorCode ?? (material as { code?: string }).code ?? material.id.toUpperCase(),
      })) as Material[]),
      furniture: normalizeFurnitureMaterials(legacy.furniture.map((item) => ({ ...item, panelOverrides: item.panelOverrides ?? {} }))),
      drawings: [],
      groups: [],
      settings: { ...legacy.settings, orthoEnabled: false },
    } as Project;
  }
  throw new Error(`Unsupported ArborCAD project schema: ${(raw as { schemaVersion?: number }).schemaVersion ?? 'missing'}`);
}
